/**
 * CourtierClientInvitePanel - Invitation d'un courtier sur un CLIENT (lead),
 * AVANT l'attribution d'un projet.
 *
 * Permet a l'equipe RealEase de partager un dossier client avec Alexandre / Artemis
 * pour qu'il fasse une analyse de capacite d'emprunt en amont.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_BROKER = {
  email: 'alexandre.marechal@artemiscourtage.com',
  name: 'Alexandre Marechal (Artemis Courtage)',
};

const VERDICT_META = {
  favorable:   { label: 'Favorable',   bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' },
  a_etudier:   { label: 'A etudier',   bg: 'bg-amber-100',   text: 'text-amber-800',   dot: 'bg-amber-500'   },
  defavorable: { label: 'Defavorable', bg: 'bg-red-100',     text: 'text-red-800',     dot: 'bg-red-500'     },
};

const CourtierClientInvitePanel = ({ leadId }) => {
  const [invited, setInvited] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState(DEFAULT_BROKER.email);
  const [name, setName] = useState(DEFAULT_BROKER.name);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [courtierAnalysis, setCourtierAnalysis] = useState(null);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/courtier/list-on-client/${leadId}`),
        axios.get(`${API}/courtier/team/courtier-client-note/${leadId}`).catch(() => ({ data: { courtier_note: null } })),
      ]);
      setInvited(Array.isArray(r1.data) ? r1.data : []);
      setCourtierAnalysis(r2.data?.courtier_note || null);
    } catch {
      setInvited([]);
      setCourtierAnalysis(null);
    } finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) return;
    setSending(true);
    setFeedback(null);
    try {
      const r = await axios.post(`${API}/courtier/invite-client/${leadId}`, {
        email: email.trim(),
        name: name.trim() || null,
        note: note.trim() || null,
      });
      const msg = r.data.created_new
        ? 'Compte courtier cree et invitation envoyee par email.'
        : 'Courtier invite sur ce client. Email de notification envoye.';
      setFeedback({ type: 'success', msg });
      setNote('');
      load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Echec de l\'invitation' });
    } finally { setSending(false); }
  };

  const revoke = async (uid, ulabel) => {
    if (!window.confirm(`Revoquer l'acces de ${ulabel} a ce client ?`)) return;
    try {
      await axios.delete(`${API}/courtier/invite-client/${leadId}/${uid}`);
      setFeedback({ type: 'success', msg: 'Acces revoque.' });
      load();
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Echec de la revocation' });
    }
  };

  return (
    <div className="realease-card p-5" data-testid="courtier-client-invite-panel">
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-4 h-4 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Partager ce client avec un courtier
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Avant meme l'attribution d'un projet, donnez acces a Alexandre / Artemis pour qu'il analyse la capacite d'emprunt.
        </p>
      </div>

      {/* Analyse du courtier (lecture seule pour l'equipe) */}
      {courtierAnalysis && (courtierAnalysis.text || courtierAnalysis.verdict) && (
        <div className="mb-3 p-3 rounded-md bg-[#A57F4F]/5 border border-[#A57F4F]/20" data-testid="courtier-client-analysis-readonly">
          <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#A57F4F]">
                Analyse du courtier {courtierAnalysis.courtier_name ? `(${courtierAnalysis.courtier_name})` : ''}
              </p>
              {courtierAnalysis.verdict && VERDICT_META[courtierAnalysis.verdict] && (
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${VERDICT_META[courtierAnalysis.verdict].bg} ${VERDICT_META[courtierAnalysis.verdict].text}`}
                  data-testid="courtier-client-verdict-badge"
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${VERDICT_META[courtierAnalysis.verdict].dot}`} />
                  {VERDICT_META[courtierAnalysis.verdict].label}
                </span>
              )}
            </div>
            <span className="text-[10px] text-gray-500">
              {courtierAnalysis.updated_at ? new Date(courtierAnalysis.updated_at).toLocaleString('fr-FR') : ''}
            </span>
          </div>
          {courtierAnalysis.text && (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{courtierAnalysis.text}</p>
          )}
        </div>
      )}

      {!loading && invited.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="courtier-client-invited-list">
          {invited.map((u) => (
            <div
              key={u.user_id}
              className="flex items-center justify-between p-2 rounded-md bg-emerald-50 border border-emerald-200"
              data-testid={`courtier-client-invited-${u.user_id}`}
            >
              <div className="flex items-center gap-2 text-sm min-w-0">
                <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="min-w-0">
                  <p className="font-medium text-gray-900 truncate">{u.name || u.email}</p>
                  <p className="text-xs text-gray-500 truncate">{u.email}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => revoke(u.user_id, u.name || u.email)}
                className="text-xs text-red-600 hover:bg-red-100 px-2 py-1 rounded transition shrink-0"
                data-testid={`courtier-client-revoke-${u.user_id}`}
              >
                Revoquer
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#13736D]/5 border border-[#13736D]/15 rounded-lg p-3">
        <p className="text-xs font-semibold text-[#13736D] uppercase tracking-wide mb-2">Inviter un courtier</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email du courtier"
            className="realease-input text-sm"
            data-testid="courtier-client-invite-email"
          />
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom complet (facultatif)"
            className="realease-input text-sm"
            data-testid="courtier-client-invite-name"
          />
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note pour le courtier (facultatif, incluse dans l'email)"
          rows={2}
          className="realease-input text-sm mb-2"
          data-testid="courtier-client-invite-note"
        />
        <div className="flex items-center justify-between gap-3">
          {feedback && (
            <p className={`text-xs ${feedback.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`}>
              {feedback.msg}
            </p>
          )}
          <button
            type="button"
            onClick={invite}
            disabled={sending || !email.trim()}
            className="ml-auto px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
            data-testid="courtier-client-invite-submit"
          >
            {sending ? 'Envoi...' : 'Inviter & envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourtierClientInvitePanel;
